import { useState, useEffect } from 'react'
import { db } from '../firebase/config'
import { collection, getDocs, doc, updateDoc, increment, addDoc, query, where, Timestamp } from 'firebase/firestore'
import { Search, Calendar, ExternalLink, Eye, X, ChevronLeft, ChevronRight, Heart, Share2, MessageCircle, Send } from 'lucide-react'

// ─── Sauts de ligne intelligents (même logique que BlogPage) ─
function smartBreaks(text) {
  if (!text) return ''
  if (/<(p|br|div|h[1-6]|ul|ol|li)[\s>/]/i.test(text)) return text

  let t = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n')

  const SECTION_EMOJIS = [
    '📅','📍','🎸','🎵','🎶','🎤','🎙','🎪','🎭','✨','💫',
    '🔔','📢','📣','⚡','🌟','🎉','🎊','🎈','🏠','📧','📞',
    '📱','💻','🌐','🔗','👥','👤','🗓','📆','🚀','❤','💌',
    '🎯','🎼','🎹','🥁','🎻','🎺','🎷','🔊','📻','🏆','🥇',
    '🏅','📸','📷','🎬','🎥','📺','⭐','🌙','☀','📝','✍',
    '💡','🔮','🚩','✈','🚂','🚢','🕐','🕑','🕒','🕓','🕔',
    '🕕','🕖','🕗','🕘','🕙','🕚','🕛','⏰'
  ]
  const emojiPat = new RegExp(
    `([^\\n])(${SECTION_EMOJIS.map(e => e.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`,
    'g'
  )
  t = t.replace(emojiPat, '$1\n$2')
  t = t.replace(/([^\n])(\d{1,2}h\d{2})/g, '$1\n$2')
  t = t.replace(/([^\n]) (–|—|-) /g, '$1\n$2 ')
  ;['Infos ','Contact ','Programme ','Invitez ','Venez ','Retrouvez '].forEach(kw => {
    t = t.replace(new RegExp(`([^\\n])(${kw})`, 'g'), '$1\n$2')
  })
  t = t.replace(/\n{3,}/g, '\n\n')
  return t.replace(/\n/g, '<br />')
}

// ─── Composant contenu article ────────────────────────────
function ArticleContent({ html }) {
  if (!html) return null
  return (
    <div
      style={{ fontSize: 'clamp(0.95rem,1.9vw,1.1rem)', lineHeight: 1.85, color: 'rgba(255,255,255,0.9)' }}
      dangerouslySetInnerHTML={{ __html: smartBreaks(html) }}
    />
  )
}

// ─── Stat badge ───────────────────────────────────────────
function StatBadge({ icon, value, label, color }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 7,
      padding: 'clamp(8px,1.4vh,11px) clamp(10px,1.8vw,16px)',
      background: `${color}14`, borderRadius: 12,
      flex: '1 1 0', justifyContent: 'center'
    }}>
      <span style={{ color, flexShrink: 0 }}>{icon}</span>
      <span style={{ fontWeight: 700, fontSize: 'clamp(0.9rem,1.8vw,1rem)' }}>{value}</span>
      <span style={{ fontSize: 'clamp(0.68rem,1.3vw,0.78rem)', color: 'rgba(255,255,255,0.48)' }}>{label}</span>
    </div>
  )
}

// ─── Page principale ──────────────────────────────────────
function PressePage({ currentUser }) {
  const [articles, setArticles]             = useState([])
  const [comments, setComments]             = useState({})
  const [loading, setLoading]               = useState(true)
  const [searchTerm, setSearchTerm]         = useState('')
  const [currentPage, setCurrentPage]       = useState(1)
  const [selectedArticle, setSelectedArticle] = useState(null)
  const [lightboxImage, setLightboxImage]   = useState(null)
  const [imgIndex, setImgIndex]             = useState(0)
  const [showShareMenu, setShowShareMenu]   = useState(false)
  const [showCommentForm, setShowCommentForm] = useState(false)
  const [commentText, setCommentText]       = useState('')
  const [submitting, setSubmitting]         = useState(false)

  const ITEMS_PER_PAGE = 10

  useEffect(() => { loadArticles() }, [])

  useEffect(() => {
    if (selectedArticle) {
      incrementViews(selectedArticle.id)
      updateMetaTags(selectedArticle)
      loadComments(selectedArticle.id)
    } else {
      resetMetaTags()
    }
  }, [selectedArticle?.id])

  // ── Meta tags ────────────────────────────────────────────
  const setMeta = (prop, content) => {
    let el = document.querySelector(`meta[property="${prop}"]`) || document.querySelector(`meta[name="${prop}"]`)
    if (!el) {
      el = document.createElement('meta')
      el.setAttribute(prop.startsWith('og:') || prop.startsWith('twitter:') ? 'property' : 'name', prop)
      document.head.appendChild(el)
    }
    el.setAttribute('content', content)
  }
  const updateMetaTags = (a) => {
    const imgs = getImages(a)
    const exc  = a.excerpt || genExcerpt(a.content, 150)
    document.title = `${a.title || 'Article'} - Radio 100% Johnny`
    setMeta('og:title', a.title || ''); setMeta('og:description', exc)
    setMeta('og:image', imgs[0] || ''); setMeta('og:type', 'article')
    setMeta('description', exc)
  }
  const resetMetaTags = () => {
    document.title = 'Radio 100% Johnny'
    setMeta('og:title', 'Radio 100% Johnny'); setMeta('description', 'Écoutez Johnny Hallyday 24h/24')
  }

  // ── Helpers ───────────────────────────────────────────────
  const getImages = (article) => {
    const imgs = []
    ;[article.cover, article.coverImage?.url || article.coverImage].forEach(v => { if (typeof v === 'string' && v) imgs.push(v) })
    ;[article.images, article.gallery, article.photos].forEach(arr => {
      if (Array.isArray(arr)) arr.forEach(img => { const u = typeof img === 'string' ? img : img?.url; if (u) imgs.push(u) })
    })
    if (article.content) {
      const re = /<img[^>]+src=["']([^"']+)["']/gi; let m
      while ((m = re.exec(article.content)) !== null) imgs.push(m[1])
    }
    return [...new Set(imgs)].filter(u => u?.startsWith('http'))
  }

  const genExcerpt = (html, max = 200) => {
    if (!html) return ''
    const t = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
    return t.length > max ? t.slice(0, max) + '...' : t
  }

  const formatDate = (date) => {
    if (!date) return ''
    try { const d = date.toDate ? date.toDate() : new Date(date); return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) }
    catch { return '' }
  }

  const getAuthor = (a) => !a ? '' : typeof a === 'string' ? a : a.name || ''

  // ── Firebase ─────────────────────────────────────────────
  const loadArticles = async () => {
    setLoading(true)
    try {
      const snap = await getDocs(collection(db, '100johnny', 'main', 'press_articles'))
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      data.sort((a, b) => {
        const toDate = x => x?.toDate ? x.toDate() : new Date(x || 0)
        return toDate(b.date) - toDate(a.date)
      })
      setArticles(data)
      const all = {}
      await Promise.all(data.map(async art => {
        try {
          const cs = await getDocs(query(collection(db, '100johnny', 'main', 'press_comments'), where('articleId', '==', art.id), where('approved', '==', true)))
          all[art.id] = cs.docs.map(d => ({ id: d.id, ...d.data() }))
        } catch { all[art.id] = [] }
      }))
      setComments(all)
    } catch (err) { console.error(err); setArticles([]) }
    finally { setLoading(false) }
  }

  const loadComments = async (articleId) => {
    try {
      const snap = await getDocs(query(collection(db, '100johnny', 'main', 'press_comments'), where('articleId', '==', articleId), where('approved', '==', true)))
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => { const toDate = x => x?.toDate ? x.toDate() : new Date(x || 0); return toDate(b.createdAt) - toDate(a.createdAt) })
      setComments(prev => ({ ...prev, [articleId]: data }))
      setSelectedArticle(prev => prev ? { ...prev, stats: { ...prev.stats, comments: data.length } } : prev)
    } catch (err) { console.error(err) }
  }

  const incrementViews = async (id) => {
    try { await updateDoc(doc(db, '100johnny', 'main', 'press_articles', id), { 'stats.views': increment(1) }) } catch {}
  }

  const handleLike = async (id) => {
    try {
      await updateDoc(doc(db, '100johnny', 'main', 'press_articles', id), { 'stats.likes': increment(1) })
      setSelectedArticle(prev => ({ ...prev, stats: { ...prev.stats, likes: (prev.stats?.likes || 0) + 1 } }))
    } catch (err) { console.error(err) }
  }

  const handleShare = async (platform, article) => {
    const url = window.location.href, title = article.title || 'Article Radio Johnny'
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter:  `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      copy: url
    }
    if (platform === 'copy') {
      try { await navigator.clipboard.writeText(url) } catch {}
      alert('✅ Lien copié !')
    } else { window.open(urls[platform], '_blank', 'width=600,height=400') }
    try {
      await updateDoc(doc(db, '100johnny', 'main', 'press_articles', article.id), { 'stats.shares': increment(1), [`stats.sharesByPlatform.${platform}`]: increment(1) })
      setSelectedArticle(prev => ({ ...prev, stats: { ...prev.stats, shares: (prev.stats?.shares || 0) + 1 } }))
    } catch {}
    setShowShareMenu(false)
  }

  const handleSubmitComment = async (e) => {
    e.preventDefault()
    if (!currentUser) { alert('⚠️ Vous devez être connecté pour commenter'); return }
    if (!commentText.trim()) { alert('⚠️ Veuillez écrire un commentaire'); return }
    setSubmitting(true)
    try {
      await addDoc(collection(db, '100johnny', 'main', 'press_comments'), {
        articleId: selectedArticle.id, authorName: currentUser.displayName || 'Anonyme',
        text: commentText, userId: currentUser.uid, createdAt: Timestamp.now(), approved: false
      })
      await updateDoc(doc(db, '100johnny', 'main', 'press_articles', selectedArticle.id), { 'stats.comments': increment(1) })
      alert('✅ Commentaire envoyé ! Il sera visible après validation.')
      setCommentText(''); setShowCommentForm(false)
    } catch (err) { console.error(err); alert('❌ Erreur lors de l\'envoi') }
    finally { setSubmitting(false) }
  }

  // ── Filtres ───────────────────────────────────────────────
  const filtered = articles.filter(a => {
    const s = searchTerm.toLowerCase()
    return !s || a.title?.toLowerCase().includes(s) || a.source?.toLowerCase().includes(s) || a.excerpt?.toLowerCase().includes(s) || getAuthor(a.author).toLowerCase().includes(s)
  })
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE)
  const paginated  = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)

  // ════════════════════════════════════════════════════════
  // VUE ARTICLE DÉTAIL
  // ════════════════════════════════════════════════════════
  if (selectedArticle) {
    const images = getImages(selectedArticle)
    const stats  = selectedArticle.stats || {}
    const articleComments = comments[selectedArticle.id] || []

    return (
      <div className="page active">
        <div style={{ maxWidth: 860, margin: '0 auto', padding: 'clamp(12px,2vw,24px)', paddingBottom: 80 }}>

          <button
            className="btn btn-secondary"
            onClick={() => { setSelectedArticle(null); setImgIndex(0); setShowCommentForm(false); setCommentText('') }}
            style={{ marginBottom: 'clamp(14px,2.5vh,22px)', display: 'inline-flex', alignItems: 'center', gap: 7 }}
          >
            ← Retour à la presse
          </button>

          <div className="glass-card" style={{ padding: 'clamp(16px,3vw,32px)', display: 'flex', flexDirection: 'column', gap: 'clamp(16px,2.8vh,24px)' }}>

            {/* Galerie image */}
            {images.length > 0 && (
              <div style={{ position: 'relative' }}>
                <img
                  src={images[imgIndex]} alt={selectedArticle.title}
                  onClick={() => setLightboxImage(images[imgIndex])}
                  style={{ width: '100%', maxHeight: 'clamp(200px,40vh,480px)', objectFit: 'cover', borderRadius: 14, cursor: 'pointer', transition: 'transform 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.01)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                />
                {images.length > 1 && (
                  <>
                    {[{ dir: -1, side: 'left', Icon: ChevronLeft }, { dir: 1, side: 'right', Icon: ChevronRight }].map(({ dir, side, Icon }) => (
                      <button key={side} onClick={() => setImgIndex(i => (i + dir + images.length) % images.length)} style={{
                        position: 'absolute', [side]: 12, top: '50%', transform: 'translateY(-50%)',
                        width: 44, height: 44, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.25)',
                        background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', cursor: 'pointer'
                      }}>
                        <Icon size={22} color="#fff" />
                      </button>
                    ))}
                    <div style={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 6 }}>
                      {images.map((_, i) => (
                        <div key={i} onClick={() => setImgIndex(i)} style={{ width: 9, height: 9, borderRadius: '50%', background: i === imgIndex ? '#00d9ff' : 'rgba(255,255,255,0.45)', cursor: 'pointer', transition: 'background 0.2s' }} />
                      ))}
                    </div>
                    <div style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(0,0,0,0.7)', padding: '5px 12px', borderRadius: 20, fontSize: '0.82rem', fontWeight: 600 }}>
                      {imgIndex + 1} / {images.length}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Badges source / catégorie */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {selectedArticle.source && (
                <span style={{ padding: '6px 14px', borderRadius: 10, fontSize: '0.88rem', fontWeight: 700, background: 'rgba(255,215,0,0.14)', border: '1px solid rgba(255,215,0,0.3)', color: '#ffd700' }}>
                  📰 {selectedArticle.source}
                </span>
              )}
              {selectedArticle.category && (
                <span style={{ padding: '6px 14px', borderRadius: 10, fontSize: '0.88rem', fontWeight: 700, background: 'rgba(0,217,255,0.14)', border: '1px solid rgba(0,217,255,0.3)', color: '#00d9ff' }}>
                  🏷️ {selectedArticle.category}
                </span>
              )}
            </div>

            {/* Titre */}
            <h1 style={{ fontSize: 'clamp(1.4rem,3.5vw,2.2rem)', fontWeight: 800, lineHeight: 1.25, margin: 0 }}>
              {selectedArticle.title || 'Article sans titre'}
            </h1>

            {/* Méta */}
            <div style={{ display: 'flex', gap: 'clamp(10px,2vw,18px)', flexWrap: 'wrap', fontSize: 'clamp(0.8rem,1.6vw,0.9rem)', color: 'rgba(255,255,255,0.55)', paddingBottom: 'clamp(12px,2vh,16px)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              {selectedArticle.author && <div>✍️ {getAuthor(selectedArticle.author)}</div>}
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Calendar size={13} /> {formatDate(selectedArticle.date || selectedArticle.createdAt)}</div>
            </div>

            {/* Stats */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <StatBadge icon={<Eye size={15}/>}           value={stats.views || 0}         label="vues"         color="#00d9ff" />
              <StatBadge icon={<Heart size={15}/>}         value={stats.likes || 0}         label="likes"        color="#ff1493" />
              <StatBadge icon={<Share2 size={15}/>}        value={stats.shares || 0}        label="partages"     color="#ffd700" />
              <StatBadge icon={<MessageCircle size={15}/>} value={articleComments.length}   label="commentaires" color="#9333ea" />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', paddingBottom: 'clamp(12px,2vh,16px)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <button onClick={() => handleLike(selectedArticle.id)} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: 'clamp(10px,1.8vh,13px) clamp(14px,2.2vw,20px)', background: 'rgba(255,20,147,0.14)', border: '1px solid rgba(255,20,147,0.35)', borderRadius: 13, color: '#ff1493', fontWeight: 700, cursor: 'pointer', fontSize: 'clamp(0.85rem,1.7vw,0.95rem)' }}>
                <Heart size={15} /> J'aime
              </button>
              <div style={{ position: 'relative' }}>
                <button onClick={() => setShowShareMenu(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: 'clamp(10px,1.8vh,13px) clamp(14px,2.2vw,20px)', background: 'rgba(255,215,0,0.14)', border: '1px solid rgba(255,215,0,0.35)', borderRadius: 13, color: '#ffd700', fontWeight: 700, cursor: 'pointer', fontSize: 'clamp(0.85rem,1.7vw,0.95rem)' }}>
                  <Share2 size={15} /> Partager
                </button>
                {showShareMenu && (
                  <div style={{ position: 'absolute', top: '110%', left: 0, zIndex: 100, background: 'rgba(10,10,10,0.97)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 14, padding: 10, minWidth: 190, boxShadow: '0 10px 40px rgba(0,0,0,0.6)' }}>
                    {[
                      { key: 'facebook', icon: '📘', label: 'Facebook'     },
                      { key: 'twitter',  icon: '🐦', label: 'Twitter/X'    },
                      { key: 'whatsapp', icon: '💬', label: 'WhatsApp'     },
                      { key: 'telegram', icon: '✈️', label: 'Telegram'     },
                      { key: 'copy',     icon: '📋', label: 'Copier le lien' },
                    ].map(p => (
                      <button key={p.key} onClick={() => handleShare(p.key, selectedArticle)} style={{ width: '100%', padding: '9px 12px', background: 'transparent', border: 'none', color: '#fff', textAlign: 'left', cursor: 'pointer', borderRadius: 8, fontSize: '0.9rem', transition: 'background 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        {p.icon} {p.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {selectedArticle.url && (
                <a href={selectedArticle.url} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: 'clamp(10px,1.8vh,13px) clamp(14px,2.2vw,20px)', textDecoration: 'none', fontSize: 'clamp(0.85rem,1.7vw,0.95rem)' }}>
                  <ExternalLink size={15} /> Article original
                </a>
              )}
            </div>

            {/* Contenu — sauts de ligne intelligents */}
            <ArticleContent html={selectedArticle.content} />

            {/* ── COMMENTAIRES ────────────────────────────── */}
            <div style={{ paddingTop: 'clamp(20px,3.5vh,30px)', borderTop: '2px solid rgba(255,255,255,0.1)' }}>
              <h2 style={{ fontSize: 'clamp(1.1rem,2.3vw,1.5rem)', fontWeight: 700, marginBottom: 'clamp(14px,2.5vh,20px)', display: 'flex', alignItems: 'center', gap: 10 }}>
                <MessageCircle size={22} color="#9333ea" /> Commentaires ({articleComments.length})
              </h2>

              {currentUser ? (
                showCommentForm ? (
                  <form onSubmit={handleSubmitComment} style={{ marginBottom: 'clamp(14px,2.5vh,20px)', padding: 'clamp(14px,2.5vh,20px)', background: 'rgba(147,51,234,0.06)', borderRadius: 14, border: '1px solid rgba(147,51,234,0.2)', display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <textarea value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Partagez votre avis..." rows={4} required
                      style={{ width: '100%', boxSizing: 'border-box', padding: 'clamp(10px,1.8vh,14px)', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 12, color: '#fff', fontSize: 'clamp(0.88rem,1.7vw,1rem)', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }} />
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button type="submit" disabled={submitting} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 7, opacity: submitting ? 0.6 : 1 }}>
                        <Send size={15} /> {submitting ? 'Envoi...' : 'Envoyer'}
                      </button>
                      <button type="button" onClick={() => { setShowCommentForm(false); setCommentText('') }} className="btn btn-secondary">Annuler</button>
                    </div>
                    <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)', margin: 0 }}>ⓘ Visible après validation par la modération</p>
                  </form>
                ) : (
                  <button onClick={() => setShowCommentForm(true)} className="btn btn-primary" style={{ marginBottom: 'clamp(14px,2.5vh,20px)', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                    <MessageCircle size={15} /> Laisser un commentaire
                  </button>
                )
              ) : (
                <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.9rem', marginBottom: 20 }}>🔐 Connectez-vous pour commenter</p>
              )}

              {articleComments.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.45)', padding: 'clamp(20px,4vh,40px)', fontSize: 'clamp(0.9rem,1.8vw,1rem)' }}>
                  Aucun commentaire pour le moment. Soyez le premier !
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(10px,1.8vh,14px)' }}>
                  {articleComments.map(c => (
                    <div key={c.id} style={{ padding: 'clamp(12px,2vh,16px)', background: 'rgba(147,51,234,0.05)', borderRadius: 13, border: '1px solid rgba(147,51,234,0.18)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 9 }}>
                        <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,#9333ea,#ec4899)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 }}>
                          {c.authorName?.charAt(0).toUpperCase() || 'A'}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 'clamp(0.85rem,1.7vw,0.95rem)' }}>{c.authorName || 'Anonyme'}</div>
                          <div style={{ fontSize: 'clamp(0.7rem,1.3vw,0.78rem)', color: 'rgba(255,255,255,0.42)' }}>{formatDate(c.createdAt)}</div>
                        </div>
                      </div>
                      {/* Commentaire avec pre-line */}
                      <p style={{ margin: 0, fontSize: 'clamp(0.88rem,1.7vw,1rem)', lineHeight: 1.65, color: 'rgba(255,255,255,0.88)', whiteSpace: 'pre-line' }}>
                        {c.text}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Lightbox */}
        {lightboxImage && (
          <div onClick={() => setLightboxImage(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20, cursor: 'pointer' }}>
            <button onClick={e => { e.stopPropagation(); setLightboxImage(null) }} style={{ position: 'absolute', top: 18, right: 18, width: 46, height: 46, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <X size={22} color="#fff" />
            </button>
            <img src={lightboxImage} alt="Plein écran" onClick={e => e.stopPropagation()} style={{ maxWidth: '90%', maxHeight: '90vh', objectFit: 'contain', borderRadius: 12 }} />
          </div>
        )}
      </div>
    )
  }

  // ════════════════════════════════════════════════════════
  // VUE LISTE
  // ════════════════════════════════════════════════════════
  return (
    <div className="page active">
      <div style={{ maxWidth: '100%', width: '100%', padding: 'clamp(12px,2vw,24px)', paddingBottom: 80, boxSizing: 'border-box' }}>

        <div className="page-header" style={{ marginBottom: 'clamp(14px,2.5vh,22px)' }}>
          <div className="page-badge">📄 PRESSE</div>
          <h1 className="page-title">Revue de Presse</h1>
          <p className="page-subtitle">Articles de presse sur Johnny</p>
        </div>

        {/* Recherche */}
        <div className="glass-card" style={{ padding: 'clamp(10px,1.8vh,14px)', marginBottom: 'clamp(12px,2vh,18px)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Search size={17} color="rgba(255,255,255,0.4)" />
            <input type="text" placeholder="Rechercher dans la presse..." value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1) }}
              style={{ flex: 1, background: 'transparent', border: 'none', color: '#fff', outline: 'none', fontSize: 'clamp(0.88rem,1.7vw,1rem)' }} />
            {searchTerm && <button onClick={() => setSearchTerm('')} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.45)', cursor: 'pointer', fontSize: '1.1rem' }}>✕</button>}
          </div>
        </div>

        {loading ? (
          <div className="loading-spinner"><div className="spinner" /><p>Chargement...</p></div>
        ) : paginated.length === 0 ? (
          <div className="empty-state"><div className="empty-icon">📄</div><p>{searchTerm ? 'Aucun article trouvé' : 'Aucun article pour le moment'}</p></div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(clamp(280px,40%,420px),1fr))', gap: 'clamp(10px,1.8vw,18px)', marginBottom: 'clamp(16px,2.8vh,24px)' }}>
              {paginated.map(article => {
                const images = getImages(article)
                const cover  = images[0]
                const stats  = article.stats || {}
                const exc    = article.excerpt || genExcerpt(article.content)

                return (
                  <div key={article.id} onClick={() => { setSelectedArticle(article); setImgIndex(0) }}
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 18, cursor: 'pointer', overflow: 'hidden', transition: 'all 0.25s', display: 'flex', flexDirection: 'column' }}
                    onMouseEnter={e => { e.currentTarget.style.border = '1px solid rgba(0,217,255,0.35)'; e.currentTarget.style.transform = 'translateY(-2px)' }}
                    onMouseLeave={e => { e.currentTarget.style.border = '1px solid rgba(255,255,255,0.1)'; e.currentTarget.style.transform = 'none' }}
                  >
                    {cover && (
                      <div style={{ position: 'relative', height: 'clamp(130px,20vh,190px)' }}>
                        <img src={cover} alt={article.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        {images.length > 1 && (
                          <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.75)', padding: '3px 9px', borderRadius: 8, fontSize: '0.72rem', fontWeight: 600 }}>
                            📷 {images.length}
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{ padding: 'clamp(12px,2vw,16px)', flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {article.source && (
                        <span style={{ display: 'inline-block', padding: '3px 9px', borderRadius: 9, fontSize: '0.7rem', fontWeight: 700, background: 'rgba(255,215,0,0.12)', border: '1px solid rgba(255,215,0,0.25)', color: '#ffd700' }}>
                          {article.source}
                        </span>
                      )}
                      <h3 style={{ fontSize: 'clamp(1rem,2.2vw,1.2rem)', fontWeight: 800, lineHeight: 1.3, margin: 0 }}>
                        {article.title || 'Article sans titre'}
                      </h3>
                      {exc && (
                        <p style={{ color: 'rgba(255,255,255,0.68)', lineHeight: 1.6, margin: 0, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontSize: 'clamp(0.82rem,1.6vw,0.92rem)' }}>
                          {exc}
                        </p>
                      )}
                      <div style={{ marginTop: 'auto', paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', gap: 'clamp(8px,1.5vw,12px)', flexWrap: 'wrap', fontSize: 'clamp(0.68rem,1.3vw,0.78rem)', color: 'rgba(255,255,255,0.42)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}><Calendar size={11} /> {formatDate(article.date || article.createdAt)}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}><Eye size={11} /> {stats.views || 0}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}><Heart size={11} /> {stats.likes || 0}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}><MessageCircle size={11} /> {comments[article.id]?.length ?? stats.comments ?? 0}</div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                <button className="btn btn-secondary" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={{ opacity: currentPage === 1 ? 0.45 : 1 }}>← Précédent</button>
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>Page {currentPage} / {totalPages}</span>
                <button className="btn btn-secondary" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={{ opacity: currentPage === totalPages ? 0.45 : 1 }}>Suivant →</button>
              </div>
            )}
            <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' }}>
              {filtered.length} article{filtered.length > 1 ? 's' : ''} trouvé{filtered.length > 1 ? 's' : ''}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default PressePage